import React, { useEffect, useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  ArrowLeft, Plus, Trash2, Pencil, GripVertical, Eye, EyeOff, Save, X, Loader2,
  ArrowUpDown, Layout, Image as ImageIcon, Type, Square, Grid3x3, BarChart3,
  Minus, Code, Sparkles, ExternalLink, AlertCircle, CheckCircle2,
  Download, ToggleLeft, ToggleRight, HelpCircle, Image, Quote, Video, MapPin, Mail, Users,
} from 'lucide-react';
import { api, formatApiError, absoluteUrl } from '../../lib/api';
import { useReorder } from '../../lib/useReorder';
import RichTextEditor from '../../components/RichTextEditor';
import ImageUploader from '../../components/ImageUploader';

const BLOCK_TYPES = [
  { kind: 'hero', label: 'Hero', desc: 'Encabezado grande con título + subtítulo + botón', icon: Layout },
  { kind: 'text', label: 'Texto rico', desc: 'Párrafos con formato (negrita, listas, links)', icon: Type },
  { kind: 'image', label: 'Imagen', desc: 'Foto con caption opcional', icon: ImageIcon },
  { kind: 'cta', label: 'CTA', desc: 'Sección de llamada a la acción con botón', icon: Square },
  { kind: 'cards', label: 'Tarjetas', desc: 'Hasta 3 tarjetas en columnas', icon: Grid3x3 },
  { kind: 'stats', label: 'Estadísticas', desc: 'Números destacados', icon: BarChart3 },
  { kind: 'gallery', label: 'Galería', desc: 'Cuadrícula de imágenes con lightbox', icon: Image },
  { kind: 'video', label: 'Video', desc: 'Embed de YouTube o Vimeo', icon: Video },
  { kind: 'accordion', label: 'FAQ / Acordeón', desc: 'Preguntas y respuestas expandibles', icon: HelpCircle },
  { kind: 'testimonials', label: 'Testimonios', desc: 'Citas de clientes o miembros', icon: Quote },
  { kind: 'map', label: 'Mapa', desc: 'Mapa embebido (Google/OpenStreetMap)', icon: MapPin },
  { kind: 'contact_form', label: 'Formulario de contacto', desc: 'Form de contacto integrado', icon: Mail },
  { kind: 'embedded_member_list', label: 'Lista de miembros', desc: 'Tarjetas con miembros del directorio (con filtros opcionales)', icon: Users },
  { kind: 'spacer', label: 'Espaciador', desc: 'Espacio en blanco vertical', icon: Minus },
  { kind: 'html', label: 'HTML libre', desc: 'Bloque crudo (avanzado)', icon: Code },
];

const PAGE_LABELS = {
  home: 'Inicio',
  about: 'Quiénes somos',
  membership: 'Membresía',
  resources: 'Recursos',
  contact: 'Contacto',
  footer: 'Pie de página',
};

function defaultDataFor(kind) {
  switch (kind) {
    case 'hero':
      return { label_es: '', label_en: '', title_es: 'Nuevo título', title_en: 'New title', subtitle_es: '', subtitle_en: '', image_url: '', cta_text_es: '', cta_text_en: '', cta_url: '' };
    case 'president_message':
      return {
        role_es: 'Mensaje de la Presidencia',
        role_en: 'Message from the Presidency',
        body_es: '<p>Escribe aquí el mensaje de la presidenta o presidente.</p>',
        body_en: '<p>Write the president\'s message here.</p>',
        name: '',
        title_es: 'Presidenta del CMIC',
        title_en: 'President of CMIC',
        period: 'Periodo 2026',
        photo_url: '',
      };
    case 'text':
      return { html_es: '<p>Escribe aquí tu contenido en español.</p>', html_en: '<p>Write your content here in English.</p>' };
    case 'image':
      return { url: '', alt_es: '', alt_en: '', caption_es: '', caption_en: '', full_width: false };
    case 'cta':
      return { title_es: 'Únete a nosotros', title_en: 'Join us', body_es: '', body_en: '', button_text_es: 'Conocer más', button_text_en: 'Learn more', button_url: '/es', variant: 'light' };
    case 'cards':
      return { title_es: '', title_en: '', items: [{ title: 'Tarjeta 1', body: 'Descripción' }, { title: 'Tarjeta 2', body: 'Descripción' }, { title: 'Tarjeta 3', body: 'Descripción' }] };
    case 'stats':
      return { items: [{ value: '97', label_es: 'Miembros', label_en: 'Members' }, { value: '42', label_es: 'Años', label_en: 'Years' }] };
    case 'gallery':
      return { title_es: '', title_en: '', items: [] };
    case 'video':
      return { title_es: '', title_en: '', url: '', caption_es: '', caption_en: '' };
    case 'accordion':
      return { title_es: 'Preguntas frecuentes', title_en: 'FAQ', items: [{ q_es: '¿Pregunta?', q_en: 'Question?', a_es: 'Respuesta.', a_en: 'Answer.' }] };
    case 'testimonials':
      return { title_es: '', title_en: '', items: [{ quote: 'Testimonio aquí.', author: 'Nombre, Cargo' }] };
    case 'map':
      return { title_es: 'Encuéntranos', title_en: 'Find us', embed_url: '', latitude: '', longitude: '', zoom: 14, caption_es: '', caption_en: '' };
    case 'contact_form':
      return { title_es: 'Contáctanos', title_en: 'Contact us', subtitle_es: '', subtitle_en: '' };
    case 'embedded_member_list':
      return { title_es: 'Nuestros intérpretes', title_en: 'Our interpreters', limit: 9, layout: 'grid', filter_language: '', filter_city: '', show_view_all: true, random_order: false, show_photo: true, show_languages: true, show_specialties: false, show_modalities: false, show_city: false };
    case 'spacer':
      return { height: 60 };
    case 'html':
      return { html_es: '', html_en: '' };
    default:
      return {};
  }
}

function summarize(block) {
  const d = block.data || {};
  if (block.kind === 'hero') return d.title_es || d.title_en || '(sin título)';
  if (block.kind === 'text') return ((d.html_es || d.html_en || '').replace(/<[^>]+>/g, '').slice(0, 80) || '(vacío)');
  if (block.kind === 'image') return d.url ? d.url.split('/').pop() : '(sin imagen)';
  if (block.kind === 'cta') return d.title_es || d.title_en || '(sin título)';
  if (block.kind === 'cards') return `${(d.items || []).length} tarjeta(s)`;
  if (block.kind === 'stats') return `${(d.items || []).length} estadística(s)`;
  if (block.kind === 'spacer') return `${d.height || 0}px`;
  if (block.kind === 'map') return d.embed_url ? 'Mapa embebido' : (d.latitude ? `lat:${d.latitude}, lng:${d.longitude}` : '(sin coordenadas)');
  if (block.kind === 'contact_form') return d.title_es || d.title_en || 'Formulario';
  if (block.kind === 'embedded_member_list') {
    const f = [d.filter_city, d.filter_language].filter(Boolean).join(', ');
    return `${d.limit || 9} miembros${f ? ' · filtros: ' + f : ''}`;
  }
  return '';
}

export default function AdminPageEditorPage() {
  const { page_key } = useParams();
  const [blocks, setBlocks] = useState([]);
  const [settings, setSettings] = useState({ replaces_default_content: false });
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [editing, setEditing] = useState(null); // block object being edited
  const [feedback, setFeedback] = useState(null);
  const [importing, setImporting] = useState(false);
  const dnd = useReorder(blocks, (b) => b.id);

  async function refresh() {
    setLoading(true);
    try {
      const [{ data: bd }, { data: sd }] = await Promise.all([
        api.get(`/admin/pages/${page_key}/blocks`),
        api.get(`/pages/${page_key}/settings`),
      ]);
      // First-time visit: auto-import the existing page content so the admin
      // sees and edits what the public visitor actually sees (WordPress-like
      // behaviour) instead of an empty editor.
      if ((bd || []).length === 0) {
        try {
          const { data: imp } = await api.post(`/admin/pages/${page_key}/import-existing`);
          await api.put(`/admin/pages/${page_key}/settings`, { replaces_default_content: true });
          showOk(`${imp.imported} bloque(s) del contenido actual cargados. Ya puedes editarlos.`);
          const [{ data: bd2 }, { data: sd2 }] = await Promise.all([
            api.get(`/admin/pages/${page_key}/blocks`),
            api.get(`/pages/${page_key}/settings`),
          ]);
          setBlocks(bd2 || []);
          setSettings(sd2 || { replaces_default_content: false });
          return;
        } catch (_) {
          // If import fails (no seed defined or already imported), fall through.
        }
      }
      setBlocks(bd || []);
      setSettings(sd || { replaces_default_content: false });
    } finally { setLoading(false); }
  }
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [page_key]);

  useEffect(() => {
    if (feedback) {
      const t = setTimeout(() => setFeedback(null), 3500);
      return () => clearTimeout(t);
    }
  }, [feedback]);

  function showOk(m) { setFeedback({ type: 'success', message: m }); }
  function showErr(m) { setFeedback({ type: 'error', message: m }); }

  async function addBlock(kind) {
    try {
      await api.post(`/admin/pages/${page_key}/blocks`, { page_key, kind, data: defaultDataFor(kind), visible: true });
      showOk(`Bloque "${kind}" agregado`);
      setShowAdd(false);
      await refresh();
    } catch (err) { showErr(formatApiError(err.response?.data?.detail)); }
  }

  async function toggleVisible(block) {
    try {
      await api.put(`/admin/pages/blocks/${block.id}`, { visible: !block.visible });
      showOk(block.visible ? 'Bloque ocultado' : 'Bloque visible');
      await refresh();
    } catch (err) { showErr(formatApiError(err.response?.data?.detail)); }
  }

  async function deleteBlock(block) {
    if (!window.confirm(`¿Eliminar este bloque (${block.kind})?`)) return;
    try {
      await api.delete(`/admin/pages/blocks/${block.id}`);
      showOk('Bloque eliminado');
      await refresh();
    } catch (err) { showErr(formatApiError(err.response?.data?.detail)); }
  }

  async function saveEdit(updatedData) {
    if (!editing) return;
    try {
      await api.put(`/admin/pages/blocks/${editing.id}`, { data: updatedData });
      showOk('Bloque actualizado');
      setEditing(null);
      await refresh();
    } catch (err) { showErr(formatApiError(err.response?.data?.detail)); }
  }

  async function saveOrder() {
    try {
      await dnd.saveOrder(async (ids) => {
        await api.put(`/admin/pages/${page_key}/blocks/reorder`, { ids });
        showOk('Orden guardado');
        await refresh();
      });
    } catch (err) { showErr('No se pudo guardar el orden'); }
  }

  async function importExisting() {
    if (!window.confirm('Esto creará bloques editables con el contenido actual de la página. ¿Continuar?')) return;
    setImporting(true);
    try {
      const { data } = await api.post(`/admin/pages/${page_key}/import-existing`);
      showOk(`${data.imported} bloque(s) importado(s) — ahora puedes editarlos.`);
      // Auto-enable replace toggle so the public page switches to the editable version.
      await api.put(`/admin/pages/${page_key}/settings`, { replaces_default_content: true });
      await refresh();
    } catch (err) {
      showErr(formatApiError(err.response?.data?.detail));
    } finally {
      setImporting(false);
    }
  }

  async function toggleReplaceDefault() {
    try {
      await api.put(`/admin/pages/${page_key}/settings`, { replaces_default_content: !settings.replaces_default_content });
      await refresh();
      showOk(settings.replaces_default_content ? 'Mostrando ahora el contenido original' : 'Reemplazando contenido original con bloques editables');
    } catch (err) { showErr(formatApiError(err.response?.data?.detail)); }
  }

  const visible = useMemo(() => {
    if (!dnd.reorderMode) return blocks;
    const byId = Object.fromEntries(blocks.map((b) => [b.id, b]));
    return dnd.orderDraft.map((id) => byId[id]).filter(Boolean);
  }, [blocks, dnd.reorderMode, dnd.orderDraft]);

  return (
    <div data-testid="admin-page-editor">
      <div className="mb-6">
        <Link to="/admin/pages" data-testid="admin-page-editor-back" className="inline-flex items-center gap-2 text-sm text-charcoal-500 hover:text-cmic-600">
          <ArrowLeft size={14} /> Volver a páginas
        </Link>
      </div>
      <div className="mb-8 flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-charcoal-800 flex items-center gap-2">
            <Layout size={22} className="text-cmic-600" />
            Editando: {PAGE_LABELS[page_key] || page_key}
          </h1>
          <p className="text-charcoal-500 text-sm mt-1">{blocks.length} bloque(s) · Los cambios se reflejan inmediatamente en el sitio público.</p>
        </div>
        <div className="flex items-center gap-2">
          {!dnd.reorderMode ? (
            <>
              <a href={`/${page_key === 'home' ? 'es' : 'es/' + (page_key === 'about' ? 'quienes-somos' : page_key === 'membership' ? 'membresia' : page_key === 'resources' ? 'recursos' : page_key === 'contact' ? 'contacto' : page_key)}`} target="_blank" rel="noopener noreferrer" data-testid="admin-page-preview-link" className="inline-flex items-center gap-2 bg-white border border-gray-200 text-charcoal-700 px-4 py-2.5 rounded-lg text-sm font-medium hover:bg-gray-50">
                <ExternalLink size={14} /> Ver página pública
              </a>
              <button onClick={dnd.enterReorder} disabled={blocks.length < 2} data-testid="admin-page-reorder-btn" className="inline-flex items-center gap-2 bg-white border border-gray-200 text-charcoal-700 px-4 py-2.5 rounded-lg text-sm font-medium hover:bg-gray-50 disabled:opacity-50">
                <ArrowUpDown size={14} /> Reordenar
              </button>
              <button onClick={() => setShowAdd(true)} data-testid="admin-page-add-block-btn" className="inline-flex items-center gap-2 bg-cmic-600 hover:bg-cmic-700 text-white px-4 py-2.5 rounded-lg text-sm font-semibold shadow-sm">
                <Plus size={14} /> Agregar bloque
              </button>
            </>
          ) : (
            <>
              <button onClick={dnd.cancelReorder} className="inline-flex items-center gap-2 bg-white border border-gray-200 text-charcoal-700 px-4 py-2.5 rounded-lg text-sm font-medium hover:bg-gray-50">
                <X size={14} /> Cancelar
              </button>
              <button onClick={saveOrder} disabled={dnd.saving} data-testid="admin-page-save-order-btn" className="inline-flex items-center gap-2 bg-emerald-500 text-white px-4 py-2.5 rounded-lg text-sm font-semibold hover:bg-emerald-600 disabled:opacity-60">
                {dnd.saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Guardar orden
              </button>
            </>
          )}
        </div>
      </div>

      {feedback && (
        <div data-testid="admin-page-editor-feedback" className={`flex items-center gap-2 px-4 py-3 rounded-xl mb-6 text-sm font-medium ${feedback.type === 'success' ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-red-50 text-red-700 border border-red-200'}`}>
          {feedback.type === 'success' ? <CheckCircle2 size={16} /> : <AlertCircle size={16} />}
          {feedback.message}
        </div>
      )}

      {/* Page-level controls: replace toggle + import existing. */}
      <div className="bg-white rounded-2xl border border-gray-200 p-4 mb-6 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <button
            type="button"
            onClick={toggleReplaceDefault}
            data-testid="admin-page-replace-toggle"
            className="flex items-center gap-2 text-charcoal-700 hover:text-cmic-600"
          >
            {settings.replaces_default_content ? <ToggleRight size={28} className="text-cmic-600" /> : <ToggleLeft size={28} className="text-charcoal-400" />}
            <span className="text-sm font-medium">
              {settings.replaces_default_content ? 'Bloques reemplazan el contenido original' : 'Bloques se muestran ADEMÁS del contenido original'}
            </span>
          </button>
        </div>
        {blocks.length === 0 && (
          <button
            onClick={importExisting}
            disabled={importing}
            data-testid="admin-page-import-existing-btn"
            className="inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-600 text-white px-4 py-2 rounded-lg text-sm font-semibold shadow-sm disabled:opacity-60"
          >
            {importing ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
            Importar contenido existente
          </button>
        )}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-24"><Loader2 className="animate-spin text-cmic-500" size={28} /></div>
      ) : blocks.length === 0 ? (
        <div className="bg-white rounded-2xl border-2 border-dashed border-gray-200 p-16 text-center">
          <Sparkles size={36} className="text-cmic-300 mx-auto mb-3" />
          <h2 className="text-lg font-semibold text-charcoal-700 mb-1">Esta página aún no tiene bloques</h2>
          <p className="text-sm text-charcoal-500 mb-6">Importa el contenido actual de la página para empezar a editarlo, o crea bloques desde cero.</p>
          <div className="flex items-center justify-center gap-3 flex-wrap">
            <button onClick={importExisting} disabled={importing} data-testid="admin-page-empty-import-btn" className="inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-600 text-white px-5 py-2.5 rounded-lg text-sm font-semibold disabled:opacity-60">
              {importing ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />} Importar contenido existente
            </button>
            <button onClick={() => setShowAdd(true)} data-testid="admin-page-empty-add-btn" className="inline-flex items-center gap-2 bg-white border border-gray-200 text-charcoal-700 hover:bg-gray-50 px-5 py-2.5 rounded-lg text-sm font-semibold">
              <Plus size={14} /> Empezar desde cero
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          {visible.map((b) => {
            const t = BLOCK_TYPES.find((x) => x.kind === b.kind);
            const Icon = t?.icon || Layout;
            return (
              <div key={b.id} data-testid={`admin-block-${b.id}`} draggable={dnd.reorderMode} {...(dnd.reorderMode ? dnd.dragProps(b.id) : {})} className={`bg-white rounded-xl border ${dnd.reorderMode ? 'border-amber-300 cursor-move' : 'border-gray-200'} p-4 flex items-center gap-4 ${!b.visible ? 'opacity-60' : ''}`}>
                {dnd.reorderMode ? <GripVertical size={18} className="text-amber-500 flex-shrink-0" /> : <div className="w-10 h-10 rounded-lg bg-cmic-50 text-cmic-600 flex items-center justify-center flex-shrink-0"><Icon size={18} /></div>}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-charcoal-800 text-sm">{t?.label || b.kind}</span>
                    {!b.visible && <span className="text-[10px] font-bold uppercase tracking-wider bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">Oculto</span>}
                  </div>
                  <p className="text-xs text-charcoal-500 mt-0.5 truncate">{summarize(b)}</p>
                </div>
                {!dnd.reorderMode && (
                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    <button onClick={() => toggleVisible(b)} data-testid={`admin-block-${b.id}-toggle-visible`} className="p-2 rounded-lg text-charcoal-500 hover:bg-gray-100" title={b.visible ? 'Ocultar' : 'Mostrar'}>
                      {b.visible ? <Eye size={16} /> : <EyeOff size={16} />}
                    </button>
                    <button onClick={() => setEditing(b)} data-testid={`admin-block-${b.id}-edit`} className="p-2 rounded-lg text-cmic-600 hover:bg-cmic-50" title="Editar">
                      <Pencil size={16} />
                    </button>
                    <button onClick={() => deleteBlock(b)} data-testid={`admin-block-${b.id}-delete`} className="p-2 rounded-lg text-red-500 hover:bg-red-50" title="Eliminar">
                      <Trash2 size={16} />
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {showAdd && (
        <AddBlockModal onClose={() => setShowAdd(false)} onPick={addBlock} />
      )}
      {editing && (
        <EditBlockModal block={editing} onClose={() => setEditing(null)} onSave={saveEdit} />
      )}
    </div>
  );
}

function AddBlockModal({ onClose, onPick }) {
  return (
    <div className="fixed inset-0 z-[80] bg-black/50 flex items-center justify-center p-4" onClick={onClose} data-testid="add-block-modal">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h2 className="font-semibold text-charcoal-800">Agregar bloque</h2>
          <button onClick={onClose} className="p-1.5 rounded-lg text-charcoal-500 hover:bg-gray-100"><X size={18} /></button>
        </div>
        <div className="p-6 grid sm:grid-cols-2 gap-3">
          {BLOCK_TYPES.map((t) => {
            const Icon = t.icon;
            return (
              <button key={t.kind} onClick={() => onPick(t.kind)} data-testid={`add-block-pick-${t.kind}`} className="text-left bg-white border border-gray-200 rounded-xl p-4 hover:border-cmic-300 hover:bg-cmic-50/40 transition-all flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg bg-cmic-50 text-cmic-600 flex items-center justify-center flex-shrink-0"><Icon size={18} /></div>
                <div>
                  <p className="font-semibold text-sm text-charcoal-800">{t.label}</p>
                  <p className="text-xs text-charcoal-500 mt-0.5 leading-relaxed">{t.desc}</p>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function EditBlockModal({ block, onClose, onSave }) {
  const [data, setData] = useState(block.data || {});
  const [saving, setSaving] = useState(false);
  const setField = (k, v) => setData((p) => ({ ...p, [k]: v }));

  async function handleSave() {
    setSaving(true);
    try { await onSave(data); } finally { setSaving(false); }
  }

  return (
    <div className="fixed inset-0 z-[80] bg-black/50 flex items-center justify-center p-4" onClick={onClose} data-testid="edit-block-modal">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 flex-shrink-0">
          <h2 className="font-semibold text-charcoal-800">Editar bloque · {block.kind}</h2>
          <button onClick={onClose} className="p-1.5 rounded-lg text-charcoal-500 hover:bg-gray-100"><X size={18} /></button>
        </div>
        <div className="flex-1 overflow-y-auto p-6 space-y-5">
          <BlockFields kind={block.kind} data={data} setField={setField} />
        </div>
        <div className="flex justify-end gap-2 px-6 py-4 border-t border-gray-200 flex-shrink-0">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg text-sm font-medium text-charcoal-600 hover:bg-gray-100">Cancelar</button>
          <button type="button" onClick={handleSave} disabled={saving} data-testid="edit-block-save-btn" className="inline-flex items-center gap-2 bg-cmic-600 hover:bg-cmic-700 text-white px-5 py-2 rounded-lg text-sm font-semibold disabled:opacity-60">
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Guardar
          </button>
        </div>
      </div>
    </div>
  );
}

function Bilingual({ labelEs, labelEn, valueEs, valueEn, onEs, onEn, multiline = false }) {
  const Input = multiline ? 'textarea' : 'input';
  return (
    <div className="grid sm:grid-cols-2 gap-3">
      <div>
        <label className="block text-xs font-medium text-charcoal-600 mb-1.5">{labelEs} <span className="text-charcoal-400">(ES)</span></label>
        <Input value={valueEs || ''} onChange={(e) => onEs(e.target.value)} rows={multiline ? 3 : undefined} className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cmic-500/30 focus:border-cmic-500" />
      </div>
      <div>
        <label className="block text-xs font-medium text-charcoal-600 mb-1.5">{labelEn} <span className="text-charcoal-400">(EN)</span></label>
        <Input value={valueEn || ''} onChange={(e) => onEn(e.target.value)} rows={multiline ? 3 : undefined} className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cmic-500/30 focus:border-cmic-500" />
      </div>
    </div>
  );
}

function BlockFields({ kind, data, setField }) {
  if (kind === 'president_message') {
    return (
      <>
        <Bilingual labelEs="Rótulo superior" labelEn="Top label" valueEs={data.role_es} valueEn={data.role_en} onEs={(v) => setField('role_es', v)} onEn={(v) => setField('role_en', v)} />
        <div>
          <label className="block text-xs font-medium text-charcoal-600 mb-1.5">Foto (opcional — si no, se muestran las iniciales del nombre)</label>
          <ImageUploader value={data.photo_url || ''} onChange={(url) => setField('photo_url', url)} />
        </div>
        <div>
          <label className="block text-xs font-medium text-charcoal-600 mb-1.5">Mensaje en Español (HTML)</label>
          <RichTextEditor value={data.body_es || ''} onChange={(v) => setField('body_es', v)} />
        </div>
        <div>
          <label className="block text-xs font-medium text-charcoal-600 mb-1.5">Mensaje en English (HTML)</label>
          <RichTextEditor value={data.body_en || ''} onChange={(v) => setField('body_en', v)} />
        </div>
        <div>
          <label className="block text-xs font-medium text-charcoal-600 mb-1.5">Nombre completo</label>
          <input value={data.name || ''} onChange={(e) => setField('name', e.target.value)} placeholder="Ej: Mtra. Aída Mendoza" className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm" />
        </div>
        <Bilingual labelEs="Cargo" labelEn="Title" valueEs={data.title_es} valueEn={data.title_en} onEs={(v) => setField('title_es', v)} onEn={(v) => setField('title_en', v)} />
        <div>
          <label className="block text-xs font-medium text-charcoal-600 mb-1.5">Periodo (opcional)</label>
          <input value={data.period || ''} onChange={(e) => setField('period', e.target.value)} placeholder="Bienio 2026-2028" className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm" />
        </div>
      </>
    );
  }
  if (kind === 'hero') {
    return (
      <>
        <Bilingual labelEs="Etiqueta superior" labelEn="Top label" valueEs={data.label_es} valueEn={data.label_en} onEs={(v) => setField('label_es', v)} onEn={(v) => setField('label_en', v)} />
        <Bilingual labelEs="Título" labelEn="Title" valueEs={data.title_es} valueEn={data.title_en} onEs={(v) => setField('title_es', v)} onEn={(v) => setField('title_en', v)} />
        <Bilingual labelEs="Subtítulo" labelEn="Subtitle" valueEs={data.subtitle_es} valueEn={data.subtitle_en} onEs={(v) => setField('subtitle_es', v)} onEn={(v) => setField('subtitle_en', v)} multiline />
        <div>
          <label className="block text-xs font-medium text-charcoal-600 mb-1.5">Imagen de fondo (URL)</label>
          <ImageUploader value={data.image_url || ''} onChange={(url) => setField('image_url', url)} />
        </div>
        <Bilingual labelEs="Texto botón" labelEn="Button text" valueEs={data.cta_text_es} valueEn={data.cta_text_en} onEs={(v) => setField('cta_text_es', v)} onEn={(v) => setField('cta_text_en', v)} />
        <div>
          <label className="block text-xs font-medium text-charcoal-600 mb-1.5">URL del botón (ej: /es/directorio o https://...)</label>
          <input value={data.cta_url || ''} onChange={(e) => setField('cta_url', e.target.value)} placeholder="/es/directorio" className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm" />
        </div>
      </>
    );
  }
  if (kind === 'text') {
    return (
      <>
        <div>
          <label className="block text-xs font-medium text-charcoal-600 mb-1.5">Contenido en Español</label>
          <RichTextEditor value={data.html_es || ''} onChange={(v) => setField('html_es', v)} />
        </div>
        <div>
          <label className="block text-xs font-medium text-charcoal-600 mb-1.5">Contenido en English</label>
          <RichTextEditor value={data.html_en || ''} onChange={(v) => setField('html_en', v)} />
        </div>
      </>
    );
  }
  if (kind === 'image') {
    return (
      <>
        <div>
          <label className="block text-xs font-medium text-charcoal-600 mb-1.5">Imagen</label>
          <ImageUploader value={data.url || ''} onChange={(url) => setField('url', url)} />
        </div>
        <Bilingual labelEs="Texto alternativo (alt)" labelEn="Alt text" valueEs={data.alt_es} valueEn={data.alt_en} onEs={(v) => setField('alt_es', v)} onEn={(v) => setField('alt_en', v)} />
        <Bilingual labelEs="Pie de foto" labelEn="Caption" valueEs={data.caption_es} valueEn={data.caption_en} onEs={(v) => setField('caption_es', v)} onEn={(v) => setField('caption_en', v)} />
        <label className="inline-flex items-center gap-2 text-sm text-charcoal-600">
          <input type="checkbox" checked={!!data.full_width} onChange={(e) => setField('full_width', e.target.checked)} className="rounded border-gray-300 text-cmic-600 focus:ring-cmic-500" />
          Ancho completo (sin margen lateral)
        </label>
      </>
    );
  }
  if (kind === 'cta') {
    return (
      <>
        <Bilingual labelEs="Título" labelEn="Title" valueEs={data.title_es} valueEn={data.title_en} onEs={(v) => setField('title_es', v)} onEn={(v) => setField('title_en', v)} />
        <Bilingual labelEs="Descripción" labelEn="Body" valueEs={data.body_es} valueEn={data.body_en} onEs={(v) => setField('body_es', v)} onEn={(v) => setField('body_en', v)} multiline />
        <Bilingual labelEs="Texto del botón" labelEn="Button text" valueEs={data.button_text_es} valueEn={data.button_text_en} onEs={(v) => setField('button_text_es', v)} onEn={(v) => setField('button_text_en', v)} />
        <div>
          <label className="block text-xs font-medium text-charcoal-600 mb-1.5">URL del botón</label>
          <input value={data.button_url || ''} onChange={(e) => setField('button_url', e.target.value)} placeholder="/es/membresia" className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm" />
        </div>
        <div>
          <label className="block text-xs font-medium text-charcoal-600 mb-1.5">Variante de color</label>
          <select value={data.variant || 'light'} onChange={(e) => setField('variant', e.target.value)} className="rounded-lg border border-gray-200 px-3 py-2 text-sm">
            <option value="light">Fondo claro</option>
            <option value="dark">Fondo oscuro</option>
          </select>
        </div>
      </>
    );
  }
  if (kind === 'cards') {
    const items = Array.isArray(data.items) ? data.items : [];
    return (
      <>
        <Bilingual labelEs="Título de sección (opcional)" labelEn="Section title" valueEs={data.title_es} valueEn={data.title_en} onEs={(v) => setField('title_es', v)} onEn={(v) => setField('title_en', v)} />
        <div className="space-y-3">
          {items.map((it, i) => (
            <div key={i} className="border border-gray-200 rounded-lg p-3 bg-gray-50/50">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-semibold text-charcoal-700">Tarjeta {i + 1}</span>
                <button type="button" onClick={() => setField('items', items.filter((_, j) => j !== i))} className="text-red-500 hover:text-red-700 p-1"><Trash2 size={14} /></button>
              </div>
              <input value={it.title || ''} onChange={(e) => setField('items', items.map((x, j) => j === i ? { ...x, title: e.target.value } : x))} placeholder="Título" className="w-full mb-2 rounded-lg border border-gray-200 px-3 py-2 text-sm" />
              <textarea value={it.body || ''} onChange={(e) => setField('items', items.map((x, j) => j === i ? { ...x, body: e.target.value } : x))} rows={2} placeholder="Descripción" className="w-full mb-2 rounded-lg border border-gray-200 px-3 py-2 text-sm" />
              <input value={it.link_url || ''} onChange={(e) => setField('items', items.map((x, j) => j === i ? { ...x, link_url: e.target.value } : x))} placeholder="URL (opcional)" className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm" />
            </div>
          ))}
          <button type="button" onClick={() => setField('items', [...items, { title: '', body: '' }])} className="inline-flex items-center gap-2 text-xs font-medium text-cmic-600 hover:text-cmic-700">
            <Plus size={14} /> Agregar tarjeta
          </button>
        </div>
      </>
    );
  }
  if (kind === 'stats') {
    const items = Array.isArray(data.items) ? data.items : [];
    return (
      <div className="space-y-3">
        {items.map((it, i) => (
          <div key={i} className="grid grid-cols-1 sm:grid-cols-3 gap-2 border border-gray-200 rounded-lg p-3 bg-gray-50/50 relative">
            <input value={it.value || ''} onChange={(e) => setField('items', items.map((x, j) => j === i ? { ...x, value: e.target.value } : x))} placeholder="Valor (ej. 97)" className="rounded-lg border border-gray-200 px-3 py-2 text-sm" />
            <input value={it.label_es || ''} onChange={(e) => setField('items', items.map((x, j) => j === i ? { ...x, label_es: e.target.value } : x))} placeholder="Etiqueta ES" className="rounded-lg border border-gray-200 px-3 py-2 text-sm" />
            <input value={it.label_en || ''} onChange={(e) => setField('items', items.map((x, j) => j === i ? { ...x, label_en: e.target.value } : x))} placeholder="Etiqueta EN" className="rounded-lg border border-gray-200 px-3 py-2 text-sm" />
            <button type="button" onClick={() => setField('items', items.filter((_, j) => j !== i))} className="absolute -top-2 -right-2 bg-white border border-red-200 text-red-500 rounded-full p-1 hover:bg-red-50"><Trash2 size={12} /></button>
          </div>
        ))}
        <button type="button" onClick={() => setField('items', [...items, { value: '', label_es: '', label_en: '' }])} className="inline-flex items-center gap-2 text-xs font-medium text-cmic-600 hover:text-cmic-700">
          <Plus size={14} /> Agregar estadística
        </button>
      </div>
    );
  }
  if (kind === 'gallery') {
    const items = Array.isArray(data.items) ? data.items : [];
    return (
      <>
        <Bilingual labelEs="Título (opcional)" labelEn="Title" valueEs={data.title_es} valueEn={data.title_en} onEs={(v) => setField('title_es', v)} onEn={(v) => setField('title_en', v)} />
        <div className="space-y-3">
          {items.map((it, i) => (
            <div key={i} className="border border-gray-200 rounded-lg p-3 bg-gray-50/50 relative">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-semibold text-charcoal-700">Imagen {i + 1}</span>
                <button type="button" onClick={() => setField('items', items.filter((_, j) => j !== i))} className="text-red-500 hover:text-red-700 p-1"><Trash2 size={14} /></button>
              </div>
              <ImageUploader value={it.url || ''} onChange={(url) => setField('items', items.map((x, j) => j === i ? { ...x, url } : x))} />
              <input value={it.caption || ''} onChange={(e) => setField('items', items.map((x, j) => j === i ? { ...x, caption: e.target.value } : x))} placeholder="Caption (opcional)" className="w-full mt-2 rounded-lg border border-gray-200 px-3 py-2 text-sm" />
            </div>
          ))}
          <button type="button" onClick={() => setField('items', [...items, { url: '', caption: '' }])} className="inline-flex items-center gap-2 text-xs font-medium text-cmic-600 hover:text-cmic-700">
            <Plus size={14} /> Agregar imagen
          </button>
        </div>
      </>
    );
  }
  if (kind === 'video') {
    return (
      <>
        <Bilingual labelEs="Título (opcional)" labelEn="Title" valueEs={data.title_es} valueEn={data.title_en} onEs={(v) => setField('title_es', v)} onEn={(v) => setField('title_en', v)} />
        <div>
          <label className="block text-xs font-medium text-charcoal-600 mb-1.5">URL de YouTube o Vimeo</label>
          <input value={data.url || ''} onChange={(e) => setField('url', e.target.value)} placeholder="https://www.youtube.com/watch?v=..." className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm" />
        </div>
        <Bilingual labelEs="Pie de video" labelEn="Caption" valueEs={data.caption_es} valueEn={data.caption_en} onEs={(v) => setField('caption_es', v)} onEn={(v) => setField('caption_en', v)} />
      </>
    );
  }
  if (kind === 'accordion') {
    const items = Array.isArray(data.items) ? data.items : [];
    return (
      <>
        <Bilingual labelEs="Título de sección" labelEn="Section title" valueEs={data.title_es} valueEn={data.title_en} onEs={(v) => setField('title_es', v)} onEn={(v) => setField('title_en', v)} />
        <div className="space-y-3">
          {items.map((it, i) => (
            <div key={i} className="border border-gray-200 rounded-lg p-3 bg-gray-50/50 relative">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-semibold text-charcoal-700">Pregunta {i + 1}</span>
                <button type="button" onClick={() => setField('items', items.filter((_, j) => j !== i))} className="text-red-500 hover:text-red-700 p-1"><Trash2 size={14} /></button>
              </div>
              <input value={it.q_es || ''} onChange={(e) => setField('items', items.map((x, j) => j === i ? { ...x, q_es: e.target.value } : x))} placeholder="Pregunta (ES)" className="w-full mb-2 rounded-lg border border-gray-200 px-3 py-2 text-sm" />
              <input value={it.q_en || ''} onChange={(e) => setField('items', items.map((x, j) => j === i ? { ...x, q_en: e.target.value } : x))} placeholder="Question (EN)" className="w-full mb-2 rounded-lg border border-gray-200 px-3 py-2 text-sm" />
              <textarea value={it.a_es || ''} onChange={(e) => setField('items', items.map((x, j) => j === i ? { ...x, a_es: e.target.value } : x))} rows={2} placeholder="Respuesta (ES)" className="w-full mb-2 rounded-lg border border-gray-200 px-3 py-2 text-sm" />
              <textarea value={it.a_en || ''} onChange={(e) => setField('items', items.map((x, j) => j === i ? { ...x, a_en: e.target.value } : x))} rows={2} placeholder="Answer (EN)" className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm" />
            </div>
          ))}
          <button type="button" onClick={() => setField('items', [...items, { q_es: '', q_en: '', a_es: '', a_en: '' }])} className="inline-flex items-center gap-2 text-xs font-medium text-cmic-600 hover:text-cmic-700">
            <Plus size={14} /> Agregar pregunta
          </button>
        </div>
      </>
    );
  }
  if (kind === 'testimonials') {
    const items = Array.isArray(data.items) ? data.items : [];
    return (
      <>
        <Bilingual labelEs="Título (opcional)" labelEn="Title" valueEs={data.title_es} valueEn={data.title_en} onEs={(v) => setField('title_es', v)} onEn={(v) => setField('title_en', v)} />
        <div className="space-y-3">
          {items.map((it, i) => (
            <div key={i} className="border border-gray-200 rounded-lg p-3 bg-gray-50/50 relative">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-semibold text-charcoal-700">Testimonio {i + 1}</span>
                <button type="button" onClick={() => setField('items', items.filter((_, j) => j !== i))} className="text-red-500 hover:text-red-700 p-1"><Trash2 size={14} /></button>
              </div>
              <textarea value={it.quote || ''} onChange={(e) => setField('items', items.map((x, j) => j === i ? { ...x, quote: e.target.value } : x))} rows={3} placeholder='"Cita textual..."' className="w-full mb-2 rounded-lg border border-gray-200 px-3 py-2 text-sm" />
              <input value={it.author || ''} onChange={(e) => setField('items', items.map((x, j) => j === i ? { ...x, author: e.target.value } : x))} placeholder="Autor (ej: María López, Empresa XYZ)" className="w-full mb-2 rounded-lg border border-gray-200 px-3 py-2 text-sm" />
              <input value={it.photo_url || ''} onChange={(e) => setField('items', items.map((x, j) => j === i ? { ...x, photo_url: e.target.value } : x))} placeholder="URL foto (opcional)" className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm" />
            </div>
          ))}
          <button type="button" onClick={() => setField('items', [...items, { quote: '', author: '' }])} className="inline-flex items-center gap-2 text-xs font-medium text-cmic-600 hover:text-cmic-700">
            <Plus size={14} /> Agregar testimonio
          </button>
        </div>
      </>
    );
  }
  if (kind === 'spacer') {
    return (
      <div>
        <label className="block text-xs font-medium text-charcoal-600 mb-1.5">Altura (en píxeles)</label>
        <input type="number" min={10} max={400} value={data.height || 60} onChange={(e) => setField('height', parseInt(e.target.value, 10) || 60)} className="w-32 rounded-lg border border-gray-200 px-3 py-2 text-sm" />
      </div>
    );
  }
  if (kind === 'map') {
    return (
      <>
        <Bilingual labelEs="Título (opcional)" labelEn="Title" valueEs={data.title_es} valueEn={data.title_en} onEs={(v) => setField('title_es', v)} onEn={(v) => setField('title_en', v)} />
        <div>
          <label className="block text-xs font-medium text-charcoal-600 mb-1.5">URL del iframe (Google Maps "Compartir → Insertar mapa" → src) — opcional</label>
          <input value={data.embed_url || ''} onChange={(e) => setField('embed_url', e.target.value)} placeholder="https://www.google.com/maps/embed?pb=!..." className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm" />
          <p className="text-[11px] text-charcoal-500 mt-1">Si dejas vacío, se usará OpenStreetMap con las coordenadas de abajo.</p>
        </div>
        <div className="grid sm:grid-cols-3 gap-3">
          <div>
            <label className="block text-xs font-medium text-charcoal-600 mb-1.5">Latitud</label>
            <input type="text" value={data.latitude || ''} onChange={(e) => setField('latitude', e.target.value)} placeholder="19.4326" className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="block text-xs font-medium text-charcoal-600 mb-1.5">Longitud</label>
            <input type="text" value={data.longitude || ''} onChange={(e) => setField('longitude', e.target.value)} placeholder="-99.1332" className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="block text-xs font-medium text-charcoal-600 mb-1.5">Zoom</label>
            <input type="number" min={1} max={20} value={data.zoom || 14} onChange={(e) => setField('zoom', parseInt(e.target.value, 10) || 14)} className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm" />
          </div>
        </div>
        <Bilingual labelEs="Leyenda debajo del mapa" labelEn="Caption" valueEs={data.caption_es} valueEn={data.caption_en} onEs={(v) => setField('caption_es', v)} onEn={(v) => setField('caption_en', v)} />
      </>
    );
  }
  if (kind === 'contact_form') {
    return (
      <>
        <Bilingual labelEs="Título" labelEn="Title" valueEs={data.title_es} valueEn={data.title_en} onEs={(v) => setField('title_es', v)} onEn={(v) => setField('title_en', v)} />
        <Bilingual labelEs="Subtítulo" labelEn="Subtitle" valueEs={data.subtitle_es} valueEn={data.subtitle_en} onEs={(v) => setField('subtitle_es', v)} onEn={(v) => setField('subtitle_en', v)} multiline />
        <p className="text-[11px] text-charcoal-500 bg-cmic-50 border border-cmic-100 rounded p-2">El formulario manda los mensajes a la pestaña <strong>Admin → Mensajes</strong> y por correo (si Brevo está configurado).</p>
      </>
    );
  }
  if (kind === 'embedded_member_list') {
    return (
      <>
        <Bilingual labelEs="Título (opcional)" labelEn="Title" valueEs={data.title_es} valueEn={data.title_en} onEs={(v) => setField('title_es', v)} onEn={(v) => setField('title_en', v)} />
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-charcoal-600 mb-1.5">Número máximo de miembros</label>
            <input type="number" min={1} max={50} value={data.limit || 9} onChange={(e) => setField('limit', parseInt(e.target.value, 10) || 9)} className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="block text-xs font-medium text-charcoal-600 mb-1.5">Disposición</label>
            <select value={data.layout || 'grid'} onChange={(e) => setField('layout', e.target.value)} className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm">
              <option value="grid">Cuadrícula</option>
              <option value="list">Lista</option>
            </select>
          </div>
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-charcoal-600 mb-1.5">Filtrar por idioma (opcional)</label>
            <input value={data.filter_language || ''} onChange={(e) => setField('filter_language', e.target.value)} placeholder="Ej: Inglés, Francés" className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="block text-xs font-medium text-charcoal-600 mb-1.5">Filtrar por ciudad (opcional)</label>
            <input value={data.filter_city || ''} onChange={(e) => setField('filter_city', e.target.value)} placeholder="Ej: Ciudad de México" className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm" />
          </div>
        </div>
        <label className="inline-flex items-center gap-2 text-sm text-charcoal-600">
          <input type="checkbox" checked={!!data.show_view_all} onChange={(e) => setField('show_view_all', e.target.checked)} className="rounded border-gray-300 text-cmic-600 focus:ring-cmic-500" />
          Mostrar enlace "Ver directorio completo" al final
        </label>
        <div className="pt-3 border-t border-gray-100">
          <label className="inline-flex items-center gap-2 text-sm font-medium text-charcoal-700 mb-3">
            <input type="checkbox" checked={!!data.random_order} onChange={(e) => setField('random_order', e.target.checked)} className="rounded border-gray-300 text-cmic-600 focus:ring-cmic-500" data-testid="member-list-random-order" />
            Mostrar en orden aleatorio (cambia en cada visita)
          </label>
          <p className="block text-xs font-medium text-charcoal-600 mb-2 mt-1">¿Qué mostrar en cada tarjeta?</p>
          <div className="grid grid-cols-2 gap-2">
            {[
              { k: 'show_photo', label: 'Foto', def: true },
              { k: 'show_languages', label: 'Idiomas', def: true },
              { k: 'show_specialties', label: 'Especialidades', def: false },
              { k: 'show_modalities', label: 'Modalidades', def: false },
              { k: 'show_city', label: 'Ciudad', def: false },
            ].map(({ k, label, def }) => (
              <label key={k} className="inline-flex items-center gap-2 text-sm text-charcoal-600">
                <input type="checkbox" checked={data[k] === undefined ? def : !!data[k]} onChange={(e) => setField(k, e.target.checked)} className="rounded border-gray-300 text-cmic-600 focus:ring-cmic-500" data-testid={`member-list-${k}`} />
                {label}
              </label>
            ))}
          </div>
        </div>
      </>
    );
  }
  if (kind === 'html') {
    return (
      <>
        <div>
          <label className="block text-xs font-medium text-charcoal-600 mb-1.5">HTML libre (ES)</label>
          <textarea value={data.html_es || ''} onChange={(e) => setField('html_es', e.target.value)} rows={8} className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm font-mono" />
        </div>
        <div>
          <label className="block text-xs font-medium text-charcoal-600 mb-1.5">HTML libre (EN)</label>
          <textarea value={data.html_en || ''} onChange={(e) => setField('html_en', e.target.value)} rows={8} className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm font-mono" />
        </div>
        <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">Nota: el HTML se sanitiza automáticamente al renderizar (no se permiten scripts).</p>
      </>
    );
  }
  return <p className="text-sm text-charcoal-500">No hay editor disponible para este tipo de bloque.</p>;
}
